import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import type { IProductionStatementApprovalAllocation } from "@/interfaces/api/production-statement-approval-allocation.interface"
import { ProductionStatementApprovalAllocation } from "@/models/production-statement-approval-allocation.model"
import { ProductionStatementApprovalAllocationRepository } from "@/repositories/production-statement-approval-allocation-repository"
import { useProductionStatementApprovalStore } from "@/store/production-statement-approvals.store"
import { useProductionStatementStore } from "@/store/production-statements.store"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import type { ProductionStatementApproval } from "@/models/production-statement-approval.model"
import type { ProductionStatement } from "@/models/production-statement.model"

export class ProductionStatementApprovalAllocationController extends GenericModelController<
  ProductionStatementApprovalAllocation,
  IProductionStatementApprovalAllocation,
  ProductionStatementApprovalAllocationRepository
> {
  constructor() {
    const productionStatementApprovalStore = useProductionStatementApprovalStore()
    const productionStatementStore = useProductionStatementStore()
    const { productionStatementApprovals } = storeToRefs(productionStatementApprovalStore)
    const { productionStatements } = storeToRefs(productionStatementStore)

    const {
      pushToRelatedObjects: pushToProductionStatementApproval,
      removeFromRelatedObjects: removeFromProductionStatementApproval
    } = useRelatedObjectsManager(
      productionStatementApprovals,
      (allocation: ProductionStatementApprovalAllocation) => allocation.productionStatementApproval,
      (productionStatementApproval: ProductionStatementApproval) => productionStatementApproval.id,
      (productionStatementApproval: ProductionStatementApproval) =>
        productionStatementApproval.productionStatementApprovalAllocations
    )
    const {
      pushToRelatedObjects: pushToProductionStatement,
      removeFromRelatedObjects: removeFromProductionStatement
    } = useRelatedObjectsManager(
      productionStatements,
      (allocation: ProductionStatementApprovalAllocation) => allocation.productionStatement,
      (productionStatement: ProductionStatement) => productionStatement.id,
      (productionStatement: ProductionStatement) =>
        productionStatement.productionStatementApprovalAllocations
    )

    function pushToRelatedObjects(allocation: ProductionStatementApprovalAllocation) {
      pushToProductionStatementApproval(allocation)
      pushToProductionStatement(allocation)
    }

    function removeFromRelatedObjects(allocation: ProductionStatementApprovalAllocation) {
      removeFromProductionStatementApproval(allocation)
      removeFromProductionStatement(allocation)
    }

    super(
      ProductionStatementApprovalAllocation,
      {
        onCreate: pushToRelatedObjects,
        onDelete: removeFromRelatedObjects
      },
      new ProductionStatementApprovalAllocationRepository()
    )

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      ProductionStatementApprovalAllocation,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }
}
